import { useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useTourData } from '../data/tour-data';
import { useTourGuide } from '../guide/tour-guide';
import { recordProductTourEventBestEffort, rememberProductTourEntryPoint } from '../analytics/product-tour-analytics';
import { clearProductTourReturnPath, getProductTourReturnPath } from '../navigation/product-tour-return';

export function TourHomePage() {
  const fixture = useTourData();
  const guide = useTourGuide();
  const [searchParams] = useSearchParams();
  const autoStartedRef = useRef(false);
  const returnPath = getProductTourReturnPath();

  useEffect(() => {
    if (searchParams.get('guided') !== '1' || guide.stateLoading || guide.active || autoStartedRef.current) return;
    autoStartedRef.current = true;
    void guide.startOrResume();
  }, [guide, searchParams]);
  const guideLabel = guide.state?.status === 'IN_PROGRESS' ? 'Resume guided tour' : guide.state?.status === 'COMPLETED' || guide.state?.status === 'SKIPPED' ? 'Take guided tour again' : 'Start guided tour';

  function startFromTourHome() {
    rememberProductTourEntryPoint('TOUR_HOME');
    void guide.startOrResume();
  }

  function exploreFreely() {
    rememberProductTourEntryPoint('TOUR_HOME');
    recordProductTourEventBestEffort({ event_type: 'TOUR_EXAMPLE_EXPLORED', entry_point: 'TOUR_HOME' });
  }
  return (
    <section className="r42-page r42-page--wide r42-tour-home">
      <span className="r42-eyebrow">Route42 Product Tour</span>
      <h1>See how Route42 turns machine evidence into persistent understanding.</h1>
      <p className="r42-tour-home__lede">{fixture.story.operator_goal}</p>

      <div className="r42-tour-home__machine">
        <div className="r42-machine-row__icon"><i className="bi bi-gear-wide-connected" aria-hidden="true" /></div>
        <div>
          <span className="r42-eyebrow">Example machine</span>
          <h2>{fixture.machine.machine_name}</h2>
          <p>{fixture.machine.machine_type} · {fixture.machine.facility_name}</p>
        </div>
      </div>

      <div className="r42-tour-home__question">
        <span className="r42-eyebrow">The question we'll investigate</span>
        <blockquote>“{fixture.story.machine_question}”</blockquote>
      </div>

      <div className="r42-tour-home__sources" aria-label="Example evidence">
        {fixture.evidence_sources.map((source) => (
          <div key={source.document_id} className="r42-tour-source-card">
            <i className={`bi ${source.evidence_type === 'IMAGE' ? 'bi-image' : source.evidence_type === 'CSV' ? 'bi-table' : 'bi-file-earmark-pdf'}`} aria-hidden="true" />
            <div><strong>{source.evidence_type}</strong><span>{source.title}</span></div>
          </div>
        ))}
      </div>

      <div className="r42-tour-home__guide-note">
        <i className="bi bi-signpost-split" aria-hidden="true" />
        <div><strong>Want the shortest path to the value?</strong><span>Follow 10 guided moments, skip anytime, or explore the example freely.</span></div>
      </div>

      {guide.stateError ? <p className="r42-tour-guide-state-note" role="status">{guide.stateError}</p> : null}

      <div className="r42-tour-home__actions">
        <button className="btn r42-btn-primary" type="button" onClick={startFromTourHome} disabled={guide.active}>{guideLabel}</button>
        <Link className="btn btn-outline-secondary" to="/tour/machine" onClick={exploreFreely}>Explore freely</Link>
        <Link className="btn btn-link" to={returnPath} onClick={clearProductTourReturnPath}>Exit tour</Link>
      </div>
    </section>
  );
}
